import { useEffect, useState } from 'react'
import { emailTestApi, type EmailTestRollbackState } from '@/services/api/emailTest'
import { dispatchCascadeStateChanged } from '@/utils/cascadeEvents'

function anfrageLabel(count: number) {
  return count === 1 ? '1 Anfrage' : `${count} Anfragen`
}

export function TestRollbackButton() {
  const [state, setState] = useState<EmailTestRollbackState | null>(null)
  const [open, setOpen] = useState(false)
  const [picking, setPicking] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    emailTestApi.preview().then(setState).catch(() => setState(null))
  }, [])

  if (!state?.visible) return null

  function onClick() {
    if (state?.picker === true && (state.sessions?.length ?? 0) > 1) {
      setNotice(null)
      setSessionId(null)
      setOpen(false)
      setPicking(true)
      return
    }
    if (state?.choice === 'confirm' && state.picker !== true) {
      setNotice(null)
      setSessionId(state.session_id ?? null)
      setPicking(false)
      setOpen(true)
      return
    }
    setOpen(false)
    setPicking(false)
    setNotice(state?.message ?? 'Es gibt nichts zurückzusetzen.')
  }

  function dismiss() {
    setOpen(false)
    setPicking(false)
  }

  function choose(id: string) {
    setSessionId(id)
    setPicking(false)
    setOpen(true)
  }

  async function confirm() {
    const result = await emailTestApi.apply(true, sessionId)
    setOpen(false)
    setPicking(false)
    setNotice(result.message ?? null)
    if ((result.cancelled ?? 0) > 0 || result.choice === 'done') {
      dispatchCascadeStateChanged()
    }
    const next = await emailTestApi.preview().catch(() => null)
    setState(next)
  }

  return (
    <>
      <div className="flex justify-center pt-4" data-placement="top-center">
        <button
          type="button"
          className="text-xl px-8 py-4 font-semibold rounded-lg bg-amber-600 text-white shadow"
          onClick={onClick}
        >
          {state.label}
        </button>
      </div>
      {notice && <p className="text-center mt-2">{notice}</p>}
      {picking && (
        <div role="dialog" aria-modal="true" aria-label="Session wählen" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <p>Session wählen</p>
            <div role="listbox" className="mt-4 flex flex-col gap-2">
              {(state.sessions ?? []).map((session) => (
                <button
                  key={session.session_id}
                  type="button"
                  role="option"
                  aria-selected={sessionId === session.session_id}
                  onClick={() => choose(session.session_id)}
                >
                  {session.period} · {anfrageLabel(session.anfrage_count)}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-center">
              <button type="button" onClick={dismiss}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
      {open && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-6 max-w-md text-center">
            <p>{state.message}</p>
            <div className="mt-4 flex justify-center gap-3">
              <button type="button" onClick={dismiss}>Abbrechen</button>
              <button type="button" onClick={confirm}>Bestätigen</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
